import type { ChatUi } from '@voll-intelligence/genui-contract';
import type { GenUiEntry } from '../../shared/ui/registry.js';
import { HotelPlannerCard } from './ui/hotel_planner_card.js';

/**
 * Entrada de catálogo do `hotel_planner` (slot chat). Enquanto o card é o último
 * da conversa, alterações válidas nos filtros refinam a lista pelo port de
 * leitura (in-place, sem turno); depois que a conversa anda (`superseded`), a
 * busca só sai como TURNO — vira mensagem e nasce um planner novo. Selecionar
 * hotel e escolher quarto sempre saem pelo dispatch do agente. Sem
 * planner-preview (o calendário é client-side, sem heatmap).
 *
 * SEM `frozenWhen`: o host não deve congelar o card INTEIRO, inclusive o drawer
 * de detalhes onde a escolha do QUARTO acontece.
 *
 * `freezeWhenSupersededBy` troca a regra de posição do shell: um `hotel_rooms`
 * (consequência de escolher um hotel AQUI) não pode matar a lista — trocar de
 * hotel tem de continuar possível. Congelam só o planner MAIS NOVO (outra busca
 * substitui esta) e o `product_checkout` (reserva em curso).
 *
 * `anchorAtLatestEmitter`: a resposta com DIRETIVAS ("só os com café") reemite
 * este card com o mesmo id (F.18 — um id novo congelaria o planner antigo e o
 * `hotel_rooms` aberto), e o leitor está nessa resposta: em thread longa a
 * lista repintada in-place na busca original ficava acima da dobra e "Atualizei
 * o cartão da lista." apontava para o vazio (prod, 2026-09-17). O predicado lê
 * as DIRETIVAS do bloco vencedor porque o mesmo id também é reemitido SEM elas —
 * o card de COLETA (id estável, um turno por campo, M1 · 1.9) e a busca vazia —
 * e esses devem ficar onde nasceram. O id igual segue não contando como planner
 * sucessor.
 */
export const hotelPlannerEntry: GenUiEntry<Extract<ChatUi, { kind: 'hotel_planner' }>> = {
  kind: 'hotel_planner',
  slot: 'chat',
  policy: {
    productOf: () => 'hotel',
    reconcileById: true,
    anchorAtLatestEmitter: (ui) => ui.directives !== undefined,
    freezeWhenSupersededBy: ['hotel_planner', 'product_checkout'],
  },
  render: ({ ui, readOnly, superseded, busy, replay, dispatch }) => (
    <HotelPlannerCard
      ui={ui}
      readOnly={readOnly}
      superseded={superseded}
      busy={busy}
      replay={replay}
      dispatch={dispatch}
    />
  ),
};
